import * as React from 'react';

import type {
  CycleCountFrequency,
  CycleCountScopeType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  NumberField,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import {
  createCycleCountScheduleAtom,
  updateCycleCountScheduleAtom,
} from '../../_graphql/mutationCycleCountSchedule';
import type { CycleCountScheduleRecord } from '../../_graphql/queryCycleCountSchedules';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';

type CycleCountScheduleDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingSchedule?: CycleCountScheduleRecord | null;
};

const FREQUENCY_OPTIONS = [
  { key: 'Daily', label: 'Daily' },
  { key: 'Weekly', label: 'Weekly' },
  { key: 'Monthly', label: 'Monthly' },
  { key: 'Quarterly', label: 'Quarterly' },
  { key: 'Annually', label: 'Annually' },
];

const SCOPE_TYPE_OPTIONS = [
  { key: 'Full', label: 'Full' },
  { key: 'Category', label: 'Category' },
  { key: 'Zone', label: 'Zone' },
  { key: 'Abc', label: 'ABC Classification' },
  { key: 'Value', label: 'Value Threshold' },
];

export const CycleCountScheduleDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingSchedule,
}: CycleCountScheduleDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [facilityId, setFacilityId] = React.useState('');
  const [name, setName] = React.useState('');
  const [frequency, setFrequency] =
    React.useState<CycleCountFrequency>('Monthly');
  const [scopeType, setScopeType] = React.useState<CycleCountScopeType>('Full');
  const [isBlindCount, setIsBlindCount] = React.useState(false);
  const [defaultTolerancePercentage, setDefaultTolerancePercentage] =
    React.useState<number>(5);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const createSchedule = useSetAtom(createCycleCountScheduleAtom({}));
  const updateSchedule = useSetAtom(updateCycleCountScheduleAtom({}));

  const isEditMode = !!editingSchedule;

  React.useEffect(() => {
    if (editingSchedule) {
      setFacilityId(editingSchedule.facilityId);
      setName(editingSchedule.name);
      setFrequency(editingSchedule.frequency);
      setScopeType(editingSchedule.scopeType);
      setIsBlindCount(editingSchedule.isBlindCount);
      setDefaultTolerancePercentage(
        Number(editingSchedule.defaultTolerancePercentage) || 5,
      );
    } else {
      setFacilityId('');
      setName('');
      setFrequency('Monthly');
      setScopeType('Full');
      setIsBlindCount(false);
      setDefaultTolerancePercentage(5);
    }
  }, [editingSchedule]);

  const handleClose = React.useCallback(() => {
    setFacilityId('');
    setName('');
    setFrequency('Monthly');
    setScopeType('Full');
    setIsBlindCount(false);
    setDefaultTolerancePercentage(5);
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!name.trim()) {
      toast.error(t.cycleCountSchedulesView.dialog.validation.nameRequired);
      return;
    }

    if (!isEditMode && !facilityId) {
      toast.error(t.cycleCountSchedulesView.dialog.validation.facilityRequired);
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editingSchedule) {
        await updateSchedule({
          id: editingSchedule.id,
          name: name.trim(),
          frequency,
          scopeType,
          isBlindCount,
          defaultTolerancePercentage,
        });
        toast.success(t.cycleCountSchedulesView.dialog.updateSuccess);
      } else {
        await createSchedule({
          facilityId,
          name: name.trim(),
          frequency,
          scopeType,
          isBlindCount,
          defaultTolerancePercentage,
        });
        toast.success(t.cycleCountSchedulesView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t.cycleCountSchedulesView.dialog.unknownError;
      toast.error(
        isEditMode
          ? t.cycleCountSchedulesView.dialog.updateError(errorMessage)
          : t.cycleCountSchedulesView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    facilityId,
    frequency,
    scopeType,
    isBlindCount,
    defaultTolerancePercentage,
    isEditMode,
    editingSchedule,
    createSchedule,
    updateSchedule,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const facilityOptions = React.useMemo(
    () => facilities.map((f) => ({ key: f.id, label: f.name })),
    [facilities],
  );

  const canSubmit =
    Boolean(name.trim()) &&
    (isEditMode || Boolean(facilityId)) &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.cycleCountSchedulesView.dialog.editTitle
    : t.cycleCountSchedulesView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.cycleCountSchedulesView.dialog.updating
      : t.cycleCountSchedulesView.dialog.update
    : isSubmitting
      ? t.cycleCountSchedulesView.dialog.creating
      : t.cycleCountSchedulesView.dialog.create;

  return (
    <ConsoleDialog
      className="@CycleCountScheduleDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          {!isEditMode && (
            <Select
              label={t.cycleCountSchedulesView.dialog.facilityLabel}
              value={facilityId}
              onChange={(key) => setFacilityId(String(key))}
              options={facilityOptions}
              placeholder={t.cycleCountSchedulesView.dialog.facilityPlaceholder}
              isRequired
            />
          )}

          {isEditMode && (
            <TextField
              label={t.cycleCountSchedulesView.dialog.facilityLabel}
              value={editingSchedule?.facilityName || ''}
              isDisabled
            />
          )}

          <TextField
            label={t.cycleCountSchedulesView.dialog.nameLabel}
            value={name}
            onChange={setName}
            placeholder={t.cycleCountSchedulesView.dialog.namePlaceholder}
            isRequired
          />

          <Select
            label={t.cycleCountSchedulesView.dialog.frequencyLabel}
            value={frequency}
            onChange={(key) => setFrequency(String(key) as CycleCountFrequency)}
            options={FREQUENCY_OPTIONS}
            isRequired
          />

          <Select
            label={t.cycleCountSchedulesView.dialog.scopeTypeLabel}
            value={scopeType}
            onChange={(key) => setScopeType(String(key) as CycleCountScopeType)}
            options={SCOPE_TYPE_OPTIONS}
            isRequired
          />

          <NumberField
            label={t.cycleCountSchedulesView.dialog.toleranceLabel}
            value={defaultTolerancePercentage}
            onChange={(value) => setDefaultTolerancePercentage(value ?? 5)}
            minValue={0}
            maxValue={100}
          />

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t.cycleCountSchedulesView.dialog.blindCountLabel}
            </span>
            <Switch isSelected={isBlindCount} onChange={setIsBlindCount} />
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.cycleCountSchedulesView.dialog.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
